import { useCallback, useRef } from "react";

/**
 * ARIA attributes and utilities for dropdown components
 */
export interface DropdownAriaProps {
  "aria-activedescendant"?: string;
  "aria-describedby"?: string;
  "aria-expanded": boolean;
  "aria-haspopup": "listbox" | "menu" | "tree" | "grid" | "dialog";
  "aria-invalid"?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

/**
 * Generate ARIA attributes for dropdown button
 */
export function useDropdownButtonAria(options: {
  activeDescendant?: string;
  ariaLabel?: string;
  describedBy?: string;
  hasError?: boolean;
  hasPopup?: "listbox" | "menu" | "tree" | "grid" | "dialog";
  id?: string;
  isOpen: boolean;
  labelledBy?: string;
}): DropdownAriaProps {
  const {
    activeDescendant,
    ariaLabel,
    describedBy,
    hasError = false,
    hasPopup = "listbox",
    id,
    isOpen,
    labelledBy,
  } = options;

  // Determine aria-label and aria-labelledby based on the logic:
  // 1. If labelledBy exists, use aria-labelledby
  // 2. If labelledBy doesn't exist, use aria-label with provided value or fallback to 'dropdown'
  const labelAriaAttributes: Pick<
    DropdownAriaProps,
    "aria-label" | "aria-labelledby"
  > = {};

  if (labelledBy) {
    labelAriaAttributes["aria-labelledby"] = labelledBy;
  } else {
    labelAriaAttributes["aria-label"] = ariaLabel || "dropdown";
  }

  return {
    "aria-expanded": isOpen,
    "aria-haspopup": hasPopup,
    ...(activeDescendant && { "aria-activedescendant": activeDescendant }),
    ...(describedBy && { "aria-describedby": describedBy }),
    ...(hasError && { "aria-invalid": true }),
    ...(id && { id }),
    ...labelAriaAttributes,
  };
}

/**
 * Generate ARIA attributes for dropdown list
 */
export function useDropdownListAria(options: {
  describedBy?: string;
  isOpen: boolean;
  labelledBy?: string;
  role?: "listbox" | "menu" | "tree" | "grid" | "dialog";
}): {
  "aria-describedby"?: string;
  "aria-hidden": boolean;
  "aria-labelledby"?: string;
  role: string;
} {
  const { describedBy, isOpen, labelledBy, role = "listbox" } = options;

  return {
    role,
    ...(labelledBy && { "aria-labelledby": labelledBy }),
    ...(describedBy && { "aria-describedby": describedBy }),
    "aria-hidden": !isOpen,
  };
}

/**
 * Generate ARIA attributes for dropdown option
 */
export function useDropdownOptionAria(options: {
  isActive: boolean;
  isSelected: boolean;
  optionId: string;
  optionValue: string;
}): {
  "aria-current": "true" | "false";
  "aria-selected": boolean;
  "data-value": string;
  id: string;
  role: string;
} {
  const { isActive, isSelected, optionId, optionValue } = options;

  return {
    role: "option",
    "aria-selected": isSelected,
    "aria-current": isActive ? "true" : "false",
    id: optionId,
    "data-value": optionValue,
  };
}

/**
 * Hook for managing dropdown keyboard navigation
 */
export function useDropdownKeyboard(options: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (index: number) => void;
  onToggle: () => void;
  selectedIndex: number;
  totalOptions: number;
}): {
  activeIndex: number;
  handleKeyDown: (event: KeyboardEvent) => void;
} {
  const { isOpen, onClose, onSelect, onToggle, selectedIndex, totalOptions } =
    options;
  const activeIndexRef = useRef(selectedIndex);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) {
        if (
          event.key === "Enter" ||
          event.key === " " ||
          event.key === "ArrowDown" ||
          event.key === "ArrowUp"
        ) {
          event.preventDefault();
          onToggle();
        }
        return;
      }

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          onClose();
          break;
        case "ArrowDown":
          event.preventDefault();
          activeIndexRef.current = Math.min(
            activeIndexRef.current + 1,
            totalOptions - 1
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          activeIndexRef.current = Math.max(activeIndexRef.current - 1, 0);
          break;
        case "Home":
          event.preventDefault();
          activeIndexRef.current = 0;
          break;
        case "End":
          event.preventDefault();
          activeIndexRef.current = totalOptions - 1;
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          onSelect(activeIndexRef.current);
          break;
        case "Tab":
          // Allow tab to close dropdown and move to next element
          onClose();
          break;
      }
    },
    [isOpen, onToggle, onSelect, onClose, totalOptions]
  );

  return {
    handleKeyDown,
    activeIndex: activeIndexRef.current,
  };
}

/**
 * ARIA attributes for dropdown label
 */
export function useDropdownLabelAria(options: {
  isRequired?: boolean;
  labelId: string;
  targetId: string;
}): {
  "aria-required"?: boolean;
  htmlFor: string;
  id: string;
} {
  const { isRequired, labelId, targetId } = options;

  return {
    htmlFor: targetId,
    id: labelId,
    ...(isRequired && { "aria-required": true }),
  };
}

/**
 * ARIA attributes for dropdown helper text
 */
export function useDropdownHelperTextAria(options: {
  hasError?: boolean;
  helperTextId: string;
}): {
  "aria-live": "polite" | "assertive";
  id: string;
  role: "alert" | "status";
} {
  const { hasError = false, helperTextId } = options;

  return {
    id: helperTextId,
    role: hasError ? "alert" : "status",
    "aria-live": hasError ? "assertive" : "polite",
  };
}
